import { useEffect, type ReactNode } from 'react'
import { injectTriggerStyles } from '../ui/trigger.css'
import { useReqdeskTrigger } from './useReqdeskTrigger'
import type { ReqdeskTriggerProps, ReactActionIcon } from './types'

const CHAT_PATH = 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z'

function DefaultChatIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={CHAT_PATH} />
    </svg>
  )
}

function renderIcon(icon: ReactActionIcon | null | undefined): ReactNode {
  if (icon === null) return null
  if (icon === undefined) return <DefaultChatIcon />
  if (typeof icon === 'string') {
    return (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d={icon} />
      </svg>
    )
  }
  if (typeof icon === 'object' && 'kind' in icon) {
    if (icon.kind === 'path') {
      return (
        <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d={icon.path} />
        </svg>
      )
    }
    if (icon.kind === 'url') {
      return <img src={icon.src} width={18} height={18} alt="" aria-hidden="true" />
    }
    // 'html' kind is vanilla-only per contract; in React we skip rather than dangerouslySet.
    return null
  }
  return icon as ReactNode
}

export function ReqdeskTrigger({
  variant = 'pill',
  icon,
  className,
  style,
  children,
  render,
  target,
  display,
}: ReqdeskTriggerProps) {
  useEffect(() => { injectTriggerStyles() }, [])
  const { onClick, isOpen, ariaProps } = useReqdeskTrigger({ target, display })

  if (render) {
    return <>{render({ onClick, isOpen, ariaProps })}</>
  }

  const classes = `rqd-trigger rqd-trigger--${variant}${className ? ` ${className}` : ''}`
  const iconNode = renderIcon(icon)
  const labelNode = variant === 'icon'
    ? <span className="rqd-trigger-label">{children ?? 'Open support'}</span>
    : (children ?? null)

  return (
    <button
      type="button"
      className={classes}
      style={style}
      onClick={onClick}
      {...ariaProps}
    >
      {iconNode}
      {labelNode}
    </button>
  )
}
